import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Control, useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';

import {
  ButtonPrimary,
  ButtonPseudo,
  PermissionDenied,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
} from '@ui/src';
import { useAppSelector } from '@store';
import useRiskScore from '../../helpers/use-risk-score';
import { RiskScoreClientTypeEnum } from '../../types';
import { DeliveryChannelFormTypes } from './types';

import ROUTES from '../../../routes';

interface FormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: string;
  clientType: string;
}

const DeliveryChannelForm = ({
  clientType,
  getList,
  item,
  updateDeliveryChannel,
  createDeliveryChannel,
  isSuccess,
  panelIsOpen,
  setPanelIsOpen,
  isLoading,
  requestInProgress,
  updateInProgress,
  updateSuccess,
}: DeliveryChannelFormTypes) => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    name: '',
    riskScoreRangeId: '',
    riskScore: '',
  });

  const {
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { channelId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      clientType,
      name: '',
      riskScoreRangeId: '',
      riskScore: '',
    },
  });

  const riskScore = watch('riskScoreRangeId');
  const name = watch('name');
  const riskScoreNumber = watch('riskScore');

  const isDirty = !isEqual(defaultValues, {
    name,
    riskScore: riskScoreNumber,
    riskScoreRangeId: riskScore,
  });

  const { onFormSubmit, form, globalError } = useRiskScore({
    handleSubmit,
    itemId: item?.id,
    setError,
    getList: (query: string) => getList(query),
    update: updateDeliveryChannel,
    create: createDeliveryChannel,
    setValue,
    errors,
    control: control as Control<any>,
    withClientType: true,
    nameTitle: 'Type',
    disabled: Boolean(
      !activePermissions?.includes(
        PermissionEnum.SETTINGS_DELIVERY_CHANNELS_EDIT_ACTION,
      ) && channelId,
    ),
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        `${
          clientType === RiskScoreClientTypeEnum.INDIVIDUAL
            ? ROUTES.INDIVIDUAL_CLIENTS_RISK_DELIVERY_CHANNELS
            : ROUTES.CORPORATE_CLIENTS_RISK_DELIVERY_CHANNELS
        }${search || ''}`,
      );
    }, 100);
  }, []);

  useEffect(() => {
    if (item) {
      reset({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item.riskScoreRangeId,
      });

      setDefaultValues({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item.riskScoreRangeId,
      });
    }
  }, [reset, item]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      setDefaultValues({
        name,
        riskScore: riskScoreNumber,
        riskScoreRangeId: riskScore,
      });

      handleClose();
    }
  }, [updateSuccess]);

  let content = form;
  let title;
  const buttonText = channelId ? 'Save channel' : 'Add channel';

  if (isLoading) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !channelId &&
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_DELIVERY_CHANNELS_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  }

  if (channelId) {
    title = 'Edit channel';

    if (
      !activePermissions?.includes(
        PermissionEnum.SETTINGS_DELIVERY_CHANNELS_EDIT_ACTION,
      )
    ) {
      title = 'Channel info';
    }
  } else {
    title = 'Add channel';
  }

  const buttonVisible = channelId
    ? activePermissions?.includes(
        PermissionEnum.SETTINGS_DELIVERY_CHANNELS_EDIT_ACTION,
      )
    : activePermissions?.includes(
        PermissionEnum.SETTINGS_DELIVERY_CHANNELS_CREATE_ACTION,
      );

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="delivery-channels"
        footer={
          <>
            {buttonVisible && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                disabled={!riskScore || !name}
                bold
              />
            )}
            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default DeliveryChannelForm;
